// Client-side analytics for sustainbit.com landing-static.
//
// Anonymous visitor_id in localStorage, no cookies, no PII. Captures the
// funnel and cohort dimensions defined in packages/content/TRACKING-PLAN.md.
// Server-side events live in functions/api/* and stitch via visitor_id.
//
// Init is no-op if VITE_POSTHOG_API_KEY is empty, so dev/preview without a
// key just runs without the network round-trips.

import { EVENTS, PosthogSink, Tracker } from '@sustainbit/tracking';

const VISITOR_KEY = 'sb-visitor-id';
const SESSION_ID_KEY = 'sb-session-id';
const SESSION_LAST_KEY = 'sb-session-last';
const SESSION_IDLE_MS = 30 * 60 * 1000;

function newId(): string {
  return (crypto.randomUUID?.() ??
    Math.random().toString(36).slice(2) + Date.now().toString(36)) as string;
}

function getOrCreateVisitorId(): string {
  let id: string | null = null;
  try {
    id = localStorage.getItem(VISITOR_KEY);
  } catch {
    // localStorage might be blocked (incognito strict). Fall through.
  }
  if (!id) {
    id = newId();
    try {
      localStorage.setItem(VISITOR_KEY, id);
    } catch {
      // ignored
    }
  }
  return id;
}

// Session ID — refreshed after 30 minutes of inactivity. PostHog Web
// Analytics needs $session_id on every event to compute Sessions/Visitors.
function getOrCreateSessionId(): string {
  const now = Date.now();
  try {
    const last = parseInt(localStorage.getItem(SESSION_LAST_KEY) ?? '0', 10);
    const existing = localStorage.getItem(SESSION_ID_KEY);
    if (existing && now - last < SESSION_IDLE_MS) {
      localStorage.setItem(SESSION_LAST_KEY, String(now));
      return existing;
    }
    const id = newId();
    localStorage.setItem(SESSION_ID_KEY, id);
    localStorage.setItem(SESSION_LAST_KEY, String(now));
    return id;
  } catch {
    return newId();
  }
}

function touchSession(): void {
  try {
    localStorage.setItem(SESSION_LAST_KEY, String(Date.now()));
  } catch {
    // ignored
  }
}

function referringDomain(ref: string): string {
  if (!ref) return '$direct';
  try {
    return new URL(ref).hostname;
  } catch {
    return '$direct';
  }
}

type ReferrerKind = 'direct' | 'search' | 'social' | 'email' | 'other';

function classifyReferrer(ref: string): ReferrerKind {
  if (!ref) return 'direct';
  try {
    const host = new URL(ref).hostname;
    if (/(^|\.)google\.|bing\.|duckduckgo\.|yandex\./.test(host)) return 'search';
    if (/twitter|x\.com|linkedin|facebook|t\.co|news\.ycombinator|reddit/.test(host))
      return 'social';
    if (/mail|outlook|gmail|protonmail/.test(host)) return 'email';
    return 'other';
  } catch {
    return 'other';
  }
}

interface Cohorts {
  device: 'mobile' | 'desktop';
  reduced_motion: boolean;
  referrer_kind: ReferrerKind;
}

function deriveCohorts(): Cohorts {
  return {
    device: window.innerWidth < 768 ? 'mobile' : 'desktop',
    reduced_motion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    referrer_kind: classifyReferrer(document.referrer),
  };
}

export interface AnalyticsHandle {
  tracker: Tracker;
  visitorId: string;
  sessionId: string;
  cohorts: Cohorts;
  /** Wrapped track that injects $session_id and refreshes session activity.
   *  Prefer this over tracker.track so the Web Analytics dashboard sees
   *  every event under the right session bucket. */
  track: (eventName: typeof EVENTS[keyof typeof EVENTS], properties?: Record<string, unknown>) => void;
  flush: () => Promise<void>;
}

export function initAnalytics(): AnalyticsHandle | null {
  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY ?? '';
  const host = import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.posthog.com';
  if (!apiKey) return null;

  const visitorId = getOrCreateVisitorId();
  const sessionId = getOrCreateSessionId();
  const cohorts = deriveCohorts();
  const sink = new PosthogSink({ apiKey, host });
  const tracker = new Tracker({
    visitorId,
    sink,
    defaultContext: {
      pageUrl: window.location.href,
      pageTitle: document.title,
      userAgent: navigator.userAgent,
      device: cohorts.device,
      reduced_motion: cohorts.reduced_motion,
      referrer_kind: cohorts.referrer_kind,
    },
  });

  // Wrap track() so every event carries $session_id (and we refresh the
  // session's last-seen timestamp). PostHog's Web Analytics dashboard needs
  // $session_id on every event to compute Sessions, Bounce, and Visitors.
  const track = (eventName: typeof EVENTS[keyof typeof EVENTS], props: Record<string, unknown> = {}) => {
    touchSession();
    tracker.track(eventName, { $session_id: sessionId, ...props });
  };

  // $pageview gets the full PostHog Web Analytics standard property suite.
  // $browser / $os / $device_type are derived server-side from $user_agent.
  track(EVENTS.PAGE_VIEW, {
    device: cohorts.device,
    reduced_motion: cohorts.reduced_motion,
    referrer_kind: cohorts.referrer_kind,
    $host: window.location.host,
    $pathname: window.location.pathname,
    $referrer: document.referrer || '$direct',
    $referring_domain: referringDomain(document.referrer),
  });
  if (cohorts.reduced_motion) {
    track(EVENTS.REDUCED_MOTION_ACTIVE, {});
  }

  // Section view + dwell via IntersectionObserver.
  const sectionDwellStart = new Map<string, number>();
  const sectionObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const id = (entry.target as HTMLElement).id;
        if (!id) continue;
        if (entry.isIntersecting) {
          if (!sectionDwellStart.has(id)) {
            sectionDwellStart.set(id, performance.now());
            track(EVENTS.SECTION_VIEW, { section_id: id });
          }
        } else {
          const start = sectionDwellStart.get(id);
          if (start !== undefined) {
            track(EVENTS.SECTION_DWELL, {
              section_id: id,
              dwell_ms: Math.round(performance.now() - start),
            });
            sectionDwellStart.delete(id);
          }
        }
      }
    },
    { threshold: 0.5 },
  );
  document.querySelectorAll<HTMLElement>('section[id]').forEach((el) => {
    sectionObserver.observe(el);
  });

  // Scroll milestones.
  const milestones = [25, 50, 75, 100];
  const firedMilestones = new Set<number>();
  let scrollTicking = false;
  window.addEventListener(
    'scroll',
    () => {
      if (scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(() => {
        const scrollable =
          document.documentElement.scrollHeight - window.innerHeight;
        const pct =
          scrollable > 0 ? Math.round((window.scrollY / scrollable) * 100) : 0;
        for (const m of milestones) {
          if (pct >= m && !firedMilestones.has(m)) {
            firedMilestones.add(m);
            track(EVENTS.SCROLL_MILESTONE, { milestone: m });
          }
        }
        scrollTicking = false;
      });
    },
    { passive: true },
  );

  // CTAs: anchor links and external links.
  document.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((a) => {
    a.addEventListener('click', () => {
      const href = a.getAttribute('href') ?? '';
      const ctaId = a.dataset.ctaId ?? href;
      if (href.startsWith('#')) {
        track(EVENTS.CTA_CLICKED, { cta_id: ctaId, href });
      } else if (/^https?:/.test(href)) {
        try {
          const dest = new URL(href);
          if (dest.origin !== window.location.origin) {
            track(EVENTS.EXTERNAL_LINK_CLICKED, { href });
          }
        } catch {
          // ignore malformed href
        }
      }
    });
  });

  // Flush on visibility change + before unload so events don't get lost.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void tracker.flush();
    }
  });
  window.addEventListener('beforeunload', () => {
    void tracker.flush();
  });

  return {
    tracker,
    visitorId,
    sessionId,
    cohorts,
    track,
    flush: () => tracker.flush(),
  };
}
